import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  ActionMenu,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  EmptyState,
  ErrorState,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
} from '@/components/ui';
import { AccountActions } from '@/features/admin/AccountActions';
import { SignInAsAction } from '@/features/admin/SignInAsAction';
import { VerifyEmailAction } from '@/features/admin/VerifyEmailAction';
import { roleLabel } from '@/features/access/roles';
import { ACCOUNT_TONES, humanizeStatus } from '@/features/admin/status';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate } from '@/utils/format';

/** The role filter, as tabs. Same values the dropdown sent. */
const ROLE_TABS = [
  { key: '', label: 'All' },
  { key: 'STUDENT', label: 'Students' },
  { key: 'COMPANY', label: 'Companies' },
  { key: 'SUPER_ADMIN', label: 'Administrators' },
];

/**
 * Company people by their role in the workspace; shown on the Companies tab.
 * The person who registered a company is one of its admins, so there is no
 * separate "Owner" choice: they are under Company admin.
 */
const COMPANY_ROLE_OPTIONS = [
  { value: 'COMPANY_ADMIN', label: 'Company admin' },
  { value: 'HR', label: 'HR' },
  { value: 'RECRUITER', label: 'Recruiter' },
  { value: 'HIRING_MANAGER', label: 'Hiring manager' },
];

const PAGE_SIZE = 20;

export function AdminUsersPage() {
  const [role, setRole] = useState('');
  const [companyRole, setCompanyRole] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(role ? { role } : {}),
    ...(role === 'COMPANY' && companyRole ? { company_role: companyRole } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-users', params],
    queryFn: () => adminApi.listUsers(params),
    // Without this every keystroke unmounts the page for the loading state,
    // taking the search box and the focus in it -- so half a filter silently
    // applies, and this is a screen with destructive buttons on every row.
    placeholderData: keepPreviousData,
  });

  const activeCount = [search !== '', role !== '', companyRole !== ''].filter(Boolean).length;

  function resetFilters() {
    setSearch('');
    setRole('');
    setCompanyRole('');
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Users"
        description="Every account on the platform. Suspending or banning one ends its sessions immediately."
      />

      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            label="Role"
            tabs={ROLE_TABS}
            value={role}
            onChange={(key) => {
              setRole(key);
              // The workspace role only means something for company people.
              if (key !== 'COMPANY') setCompanyRole('');
              setPage(1);
            }}
          />
        }
        toolbarEnd={
          <>
            {role === 'COMPANY' && (
              <Select
                label="Workspace role"
                hideLabel
                size="sm"
                wrapperClassName="w-full sm:w-48"
                value={companyRole}
                onChange={(event) => {
                  setCompanyRole(event.target.value);
                  setPage(1);
                }}
              >
                <option value="">Any workspace role</option>
                {COMPANY_ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
            <SearchInput
              label="Search users"
              hideLabel
              size="sm"
              className="w-full sm:w-72"
              placeholder="Search by name or email"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading users…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="No users"
                description="Nothing matches these filters."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No users yet"
                description="Accounts appear here as people sign up."
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => (
            <BoxRow
              key={row.id}
              accent={ACCOUNT_TONES[row.status] ?? 'neutral'}
              leading={<BoxAvatar name={row.full_name} round />}
              title={row.full_name}
              badges={
                <Badge dot tone={ACCOUNT_TONES[row.status] ?? 'neutral'}>
                  {humanizeStatus(row.status)}
                </Badge>
              }
              subtitle={row.email}
              facts={[
                { icon: 'user', text: humanizeStatus(row.role) },
                row.company_name && { icon: 'building', text: row.company_name },
                row.company_role && {
                  icon: 'idBadge',
                  text:
                    COMPANY_ROLE_OPTIONS.find((option) => option.value === row.company_role)
                      ?.label ?? roleLabel(row.company_role),
                },
                { icon: 'calendar', text: `Joined ${formatDate(row.created_at)}` },
              ].filter(Boolean)}
              // No record page to open, so "Sign in as" is the way in and sits
              // last. Verify email shows only for a stuck account, where it is
              // the fix; the account decisions are rarer and go in "More".
              actions={
                <CardActions>
                  <VerifyEmailAction account={row} compact />
                  <ActionMenu label={`More actions for ${row.full_name}`}>
                    <AccountActions account={row} menu />
                  </ActionMenu>
                  <SignInAsAction account={row} compact />
                </CardActions>
              }
            />
          ))}
        </BoxList>
      </ListCard>
    </div>
  );
}
